'use client';
import { useState, useRef, ChangeEvent, useEffect } from 'react';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/navigation';
import { useAnalytics } from '@/lib/analytics';
import { usePdfTool } from '@/context/PdfToolContext';
import { 
  DndContext, 
  closestCenter, 
  KeyboardSensor, 
  PointerSensor, 
  useSensor, 
  useSensors, 
  DragEndEvent 
} from '@dnd-kit/core';
import { 
  arrayMove, 
  SortableContext, 
  sortableKeyboardCoordinates, 
  rectSortingStrategy,
  useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const PdfThumbnail = dynamic(() => import('@/components/PdfThumbnail'), { ssr: false });

type PdfItem = {
  id: string;
  file: File;
};

// Sortable Tile Component
function SortablePdfTile({ item, onRemove }: { item: PdfItem; onRemove: (id: string) => void }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : 1,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="pdf-tile">
      <button 
        className="pdf-tile-remove" 
        onClick={(e) => { e.stopPropagation(); onRemove(item.id); }}
      >✕</button>
      <div className="pdf-tile-preview" {...attributes} {...listeners}>
        <PdfThumbnail file={item.file} />
      </div>
      <div className="pdf-tile-name" title={item.file.name}>{item.file.name}</div>
    </div>
  );
}

interface MergePDFClientProps {
  h1: string;
  subtitle: string;
  content: string;
}

export default function MergePDFClient({ h1, subtitle, content }: MergePDFClientProps) {
  useAnalytics('merge');
  const { items, setItems, resetContext } = usePdfTool();
  const router = useRouter();
  
  const [isDraggingOverall, setIsDraggingOverall] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Clear any existing files or jobs whenever the user visits this page
  useEffect(() => {
    resetContext();
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [resetContext]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      }
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle Full Page Drag & Drop
  useEffect(() => {
    const handleWindowDragOver = (e: DragEvent) => {
      e.preventDefault();
      if (!e.dataTransfer?.types.includes('Files')) return;
      setIsDraggingOverall(true);
    };
    
    const handleWindowDragLeave = (e: DragEvent) => {
      e.preventDefault();
      if (e.relatedTarget === null || e.clientX === 0 || e.clientY === 0) {
        setIsDraggingOverall(false);
      }
    };
    
    const handleWindowDrop = (e: DragEvent) => {
      e.preventDefault();
      setIsDraggingOverall(false);
      
      if (e.dataTransfer && e.dataTransfer.files) {
        addFiles(Array.from(e.dataTransfer.files));
      }
    };

    window.addEventListener('dragover', handleWindowDragOver);
    window.addEventListener('dragleave', handleWindowDragLeave);
    window.addEventListener('drop', handleWindowDrop);

    return () => {
      window.removeEventListener('dragover', handleWindowDragOver);
      window.removeEventListener('dragleave', handleWindowDragLeave);
      window.removeEventListener('drop', handleWindowDrop);
    };
  }, []);

  const addFiles = (newFiles: File[]) => {
    const pdfFiles = newFiles.filter(f => f.type === 'application/pdf');
    if (pdfFiles.length === 0) return;
    
    const newPdfItems = pdfFiles.map(file => ({
      id: crypto.randomUUID(),
      file
    }));
    
    const combined = [...items, ...newPdfItems];
    if (combined.length > 20) {
      setError("Maximum 20 files allowed.");
      return;
    }
    
    setError(null);
    setItems(combined);
  };

  const handleFileSelect = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      addFiles(Array.from(e.target.files));
    }
  };

  const removeFile = (id: string) => {
    setItems(items.filter((item: PdfItem) => item.id !== id));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setItems((items: PdfItem[]) => {
        const oldIndex = items.findIndex(item => item.id === active.id);
        const newIndex = items.findIndex(item => item.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const handleMergeStart = () => {
    if (items.length < 2) {
      setError("Please add at least 2 PDF files to merge.");
      return;
    }
    router.push('/merge-pdf/processing');
  };

  return (
    <div className="container tool-page">
      <div className="tool-header">
        <h1>{h1}</h1>
        <p>{subtitle}</p>
      </div>

      <div className="tool-interface">
        {items.length === 0 && (
          <div 
            className={`upload-zone ${isDraggingOverall ? 'dragging' : ''}`}
            onClick={() => fileInputRef.current?.click()}
          >
            <div className="upload-icon">📄</div>
            <h3>Select PDFs to merge/ Drag & Drop</h3>
          </div>
        )}

        {items.length > 0 && (
          <div className="pdf-workspace">
            <div className="workspace-header-row">
              <div className="workspace-header">
                <h3>Files to Merge ({items.length}/20)</h3>
                <p>Drag tiles to reorder. First tile is the first page of your new PDF.</p>
              </div>
              {items.length < 20 && (
                <button 
                  className="btn-add-more" 
                  onClick={() => fileInputRef.current?.click()}
                  title="Add more PDFs"
                >
                  + Add More
                </button>
              )}
            </div>
            
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={items.map((i: PdfItem) => i.id)} strategy={rectSortingStrategy}>
                <div className="pdf-grid">
                  {items.map((item: PdfItem) => (
                    <SortablePdfTile key={item.id} item={item} onRemove={removeFile} />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
            
            <button 
              className="btn btn-primary merge-btn"
              onClick={handleMergeStart}
              disabled={items.length < 2}
            >
              Merge PDFs
            </button>
          </div>
        )}

        <input 
          type="file" 
          multiple 
          accept=".pdf,application/pdf"
          className="hidden-input" 
          ref={fileInputRef}
          onChange={handleFileSelect}
        />

        {error && <div className="error-alert">{error}</div>}
      </div>
      
      <div className="tool-content" dangerouslySetInnerHTML={{ __html: content }}>
      </div>
    </div>
  );
}
